/**
 * DemoPage — public, no-auth "paste-a-verse → slides in ~15s" experience.
 *
 * Purpose: let landing-page visitors *try* SlideCon without signing up. The
 * whole flow lives on one page:
 *   1. Verse input + tone chip picker + Generate button
 *   2. Loading state (mirrors the hero animation phases so it feels intentional)
 *   3. Result: outline points + full-bleed carousel of the 4-6 slides
 *   4. Sign-up CTA ("Save your work to your library") that stores the demo
 *      payload in localStorage['pending_demo']; the dashboard picks it up on
 *      first mount and materializes it into a real project via
 *      /api/pulpit/send-to-editor.
 *
 * Rate limiting is enforced server-side (3/hour/IP). We surface the
 * `attempts_used` / `attempts_max` counter in the UI so people know.
 */
import { useState, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Sparkles, Loader2, BookOpen, ChevronRight, ChevronLeft,
  FileText, Save, RotateCcw, Share2, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getErrorMessage } from "../utils/errorHandler";

const API = process.env.REACT_APP_BACKEND_URL;

const EXAMPLE_VERSES = ["Romans 8:28", "Psalm 23", "John 3:16", "Philippians 4:6-7", "Matthew 6:33"];
const TONES = [
  { value: "encouraging",  label: "Encouraging" },
  { value: "expository",   label: "Expository" },
  { value: "prophetic",    label: "Prophetic" },
  { value: "evangelistic", label: "Evangelistic" },
];

function SlideCard({ slide, index, total }) {
  const isTitle = slide.kind === "title";
  const isVerse = slide.kind === "verse";
  const isCTA = slide.kind === "cta";
  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.35 }}
      className="w-full aspect-[16/9] rounded-2xl overflow-hidden relative flex flex-col justify-center px-10 sm:px-14"
      style={{
        background: isTitle
          ? "linear-gradient(135deg, #C47053 0%, #A35A40 100%)"
          : isCTA
            ? "linear-gradient(135deg, #1C1917 0%, #292524 100%)"
            : isVerse
              ? "linear-gradient(135deg, #FAF6F0 0%, #EADEC9 100%)"
              : "#ffffff",
        border: !isTitle && !isCTA ? "1px solid rgba(28, 25, 23, 0.08)" : "none",
        boxShadow: "0 12px 40px -12px rgba(28,25,23,0.18)",
      }}
      data-testid={`demo-slide-${index}`}
    >
      <p className={`text-[10px] tracking-[0.22em] uppercase font-semibold mb-3 ${
        isTitle || isCTA ? "text-white/70" : "text-stone-500"
      }`}>
        {slide.kind || "point"}
      </p>
      <h3 className={`font-display leading-tight mb-4 ${
        isTitle || isCTA
          ? "text-white text-4xl sm:text-5xl"
          : "text-stone-900 text-3xl sm:text-4xl"
      }`}>
        {slide.heading}
      </h3>
      <p className={`text-lg leading-relaxed max-w-3xl ${
        isTitle ? "text-white/90" : isCTA ? "text-stone-300" : isVerse ? "italic text-stone-700" : "text-stone-700"
      }`}>
        {slide.body}
      </p>
      <div className={`absolute bottom-4 right-6 text-xs font-mono ${
        isTitle || isCTA ? "text-white/60" : "text-stone-400"
      }`}>
        {index + 1} / {total}
      </div>
    </motion.div>
  );
}

export default function DemoPage() {
  const navigate = useNavigate();
  const [verse, setVerse] = useState("Romans 8:28");
  const [tone, setTone] = useState("encouraging");
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [result, setResult] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const slideAreaRef = useRef(null);

  const generate = async () => {
    if (!verse.trim()) {
      toast.error("Please enter a Bible verse reference");
      return;
    }
    setBusy(true);
    setResult(null);
    setSlideIdx(0);
    try {
      const res = await axios.post(`${API}/api/demo/from-verse`, {
        verse: verse.trim(),
        tone,
      });
      setResult(res.data);
      toast.success("Your teaser deck is ready!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "rate_limited") {
        toast.error(detail.message || "Demo limit reached — sign up to keep going.");
      } else {
        toast.error(getErrorMessage(err, "Generation failed. Try Romans 8:28 or Psalm 23."));
      }
    } finally {
      setBusy(false);
    }
  };

  const shareSlide = async () => {
    if (!result || !slideAreaRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(slideAreaRef.current, {
        useCORS: true, // remote fonts / images
        scale: 2,      // retina crispness
        backgroundColor: null,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const filename = `slidecon-${(result.verse || "verse").replace(/\s+/g, "-").toLowerCase()}.png`;

      // Try Web Share API on mobile with file support
      if (navigator.canShare && typeof navigator.share === "function") {
        try {
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
          const file = new File([blob], filename, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: result.title || "SlideCon",
              text: `${result.title} — try it yourself at slidecon.app/demo`,
            });
            toast.success("Shared!");
            return;
          }
        } catch (shareErr) {
          if (shareErr?.name === "AbortError") { return; } // user cancelled
          // fall through to download
        }
      }

      // Desktop / no Web Share → download
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Slide downloaded — share it wherever you like.");
    } catch (err) {
      toast.error("Couldn't render the slide. Try again in a moment.");
    } finally {
      setSharing(false);
    }
  };
  const saveAndSignUp = () => {
    if (!result) return;
    try {
      localStorage.setItem("pending_demo", JSON.stringify({
        title: result.title,
        outline: result.outline,
        big_idea: result.big_idea,
        scripture_main: result.scripture_ref,
        slides: result.slides,
        verse: result.verse,
        tone: result.tone,
        saved_at: Date.now(),
      }));
    } catch { /* localStorage full or blocked */ }
    navigate("/login?intent=save_demo");
  };

  const totalSlides = result?.slides?.length || 0;

  return (
    <div className="landing-page min-h-screen">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-stone-200/60">
        <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="demo-back-to-home">
            <ArrowLeft className="w-4 h-4 text-stone-600" />
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#C47053" }}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <span className="font-display text-xl font-semibold">SlideCon</span>
          </a>
          <div className="text-[11px] text-stone-500">
            <span className="lp-pill" style={{ backgroundColor: "#EADEC9" }}>
              <Sparkles className="w-3 h-3" /> Live demo
            </span>
          </div>
        </nav>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 sm:py-16">
        {/* ─── Input card ────────────────────────────────────────────── */}
        {!result && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="lp-overline mb-4">No sign-up, no setup</p>
            <h1 className="font-display text-4xl sm:text-6xl font-semibold leading-tight mb-4">
              Paste a verse.
              <span className="block italic text-stone-600">Get slides in about 15 seconds.</span>
            </h1>
            <p className="text-stone-600 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
              This is exactly what SlideCon does inside the app &mdash; only faster because
              we&apos;re skipping login. Try a favorite verse and see the magic.
            </p>

            <div className="lp-card-solid p-6 sm:p-8 max-w-2xl mx-auto text-left">
              <label className="lp-overline block mb-2" htmlFor="demo-verse-input">Bible verse reference</label>
              <Input
                id="demo-verse-input"
                data-testid="demo-verse-input"
                value={verse}
                onChange={(e) => setVerse(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !busy) generate(); }}
                placeholder="Romans 8:28"
                disabled={busy}
                className="text-lg h-12 bg-white border-stone-200 focus-visible:ring-[#C47053]"
              />

              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className="text-[10px] uppercase tracking-wider text-stone-500 self-center mr-1">Try:</span>
                {EXAMPLE_VERSES.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVerse(v)}
                    disabled={busy}
                    data-testid={`demo-example-${v.replace(/[^a-z0-9]/gi, "-")}`}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 disabled:opacity-40"
                  >
                    {v}
                  </button>
                ))}
              </div>

              <label className="lp-overline block mt-6 mb-2">Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTone(t.value)}
                    disabled={busy}
                    data-testid={`demo-tone-${t.value}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      tone === t.value
                        ? "bg-[#C47053] border-[#C47053] text-white"
                        : "bg-white border-stone-300 text-stone-700 hover:bg-stone-100"
                    } disabled:opacity-40`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <Button
                onClick={generate}
                disabled={busy || !verse.trim()}
                className="lp-btn-primary w-full mt-6 h-12 text-base gap-2"
                data-testid="demo-generate-btn"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Building your deck…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Show me the slides
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
              <p className="text-[11px] text-stone-500 text-center mt-3">
                3 free demos per hour. No credit card, no email required.
              </p>
            </div>
          </motion.section>
        )}

        {/* ─── Result — outline + slide carousel ─────────────────────── */}
        {result && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="demo-result"
          >
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
              <div>
                <p className="lp-overline mb-2">
                  <BookOpen className="w-3 h-3 inline mr-1" />
                  {result.scripture_ref}
                </p>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-stone-900">
                  {result.title}
                </h2>
                {result.big_idea && (
                  <p className="text-stone-600 mt-2 text-base italic max-w-2xl">{result.big_idea}</p>
                )}
              </div>
              <Button
                onClick={() => { setResult(null); setSlideIdx(0); }}
                className="lp-btn-ghost text-sm gap-2"
                data-testid="demo-try-another"
              >
                <RotateCcw className="w-4 h-4" /> Try another
              </Button>
            </div>

            {/* Carousel */}
            <div className="relative">
              <div ref={slideAreaRef}>
                <AnimatePresence mode="wait">
                  <SlideCard key={slideIdx} slide={result.slides[slideIdx]} index={slideIdx} total={totalSlides} />
                </AnimatePresence>
              </div>

              {/* Prev/next controls */}
              {totalSlides > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    onClick={() => setSlideIdx((i) => Math.max(0, i - 1))}
                    disabled={slideIdx === 0}
                    className="lp-btn-ghost text-sm gap-1"
                    data-testid="demo-prev-slide"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </Button>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {result.slides.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSlideIdx(i)}
                          aria-label={`Go to slide ${i + 1}`}
                          className={`h-1.5 rounded-full transition-all ${
                            i === slideIdx ? "w-8 bg-[#C47053]" : "w-1.5 bg-stone-300 hover:bg-stone-400"
                          }`}
                        />
                      ))}
                    </div>
                    <Button
                      onClick={shareSlide}
                      disabled={sharing}
                      className="lp-btn-ghost text-sm gap-1.5"
                      data-testid="demo-share-slide-btn"
                      title="Save the current slide as an image"
                    >
                      {sharing
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : (typeof navigator !== "undefined" && navigator.share
                            ? <Share2 className="w-4 h-4" />
                            : <Download className="w-4 h-4" />)}
                      <span className="hidden sm:inline">{sharing ? "Preparing…" : "Share slide"}</span>
                    </Button>
                  </div>
                  <Button
                    onClick={() => setSlideIdx((i) => Math.min(totalSlides - 1, i + 1))}
                    disabled={slideIdx === totalSlides - 1}
                    className="lp-btn-ghost text-sm gap-1"
                    data-testid="demo-next-slide"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Outline preview + Save CTA */}
            <div className="mt-10 grid md:grid-cols-[1.2fr_1fr] gap-6">
              <div className="lp-card-solid p-6">
                <p className="lp-overline mb-3">3-point outline</p>
                <ul className="space-y-2">
                  {(result.outline || []).map((pt, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="font-mono text-[#C47053] font-semibold flex-shrink-0">{i + 1}.</span>
                      <span className="text-stone-700 leading-relaxed">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div
                className="rounded-2xl p-6 text-white flex flex-col"
                style={{ background: "linear-gradient(135deg, #1C1917 0%, #292524 100%)" }}
              >
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/60 mb-2">Like what you see?</p>
                <h3 className="font-display text-2xl font-semibold leading-tight mb-2">
                  Save this to your library
                </h3>
                <p className="text-sm text-stone-300 leading-relaxed mb-5 flex-1">
                  Sign up (it&apos;s free) and this deck opens in the SlideCon editor so you can
                  tweak it, present it, or export to PowerPoint.
                </p>
                <Button
                  onClick={saveAndSignUp}
                  className="lp-btn-primary text-sm gap-2 w-full justify-center"
                  data-testid="demo-save-signup-btn"
                >
                  <Save className="w-4 h-4" />
                  Save &amp; sign up free
                </Button>
                <p className="text-[10px] text-white/50 text-center mt-2">
                  {result.attempts_used}/{result.attempts_max} free demos used this hour
                </p>
              </div>
            </div>
          </motion.section>
        )}
      </main>
    </div>
  );
}
